import { Preference, readWhen } from "./when";
import { readAsk } from "./spans";

/**
 * The questions the page may ask a visitor before it asks anybody for time.
 *
 * <h3>Why the set is fixed, and why the loop runs first</h3>
 * <p>A clarification loop that adapts to a host's conditions leaks them by construction (D-66). Ask
 * "what is your work email domain?" because a rule tests a domain, and you have described that rule
 * to a stranger — the oracle D-44 forbids, spread over several turns instead of one, and no amount
 * of care with the wording repairs it.</p>
 *
 * <p>So the guarantee is structural rather than careful: <b>every question is asked before the
 * first call, and the loop never reopens after one</b> (D-80). Nothing has been asked of any host
 * when these are put, so there is nothing about any host for them to be derived from. A question
 * after a refusal would be a different thing entirely, however innocent it looked, because by then
 * there would be an answer it could be reacting to.</p>
 *
 * <p>Three of them are just what a request needs to exist at all: who, what, how long. Not a
 * screening interview.</p>
 *
 * <h3>The fourth</h3>
 *
 * <p>A timeline is not in that category — unlike the other three it has a default, so a request
 * exists without it. It stays inside the rule on one condition, which the wording carries: <b>"whenever"
 * is a real answer</b>. Asked of everybody, in a fixed place, answerable in one word, and never
 * blocking. And it is a preference rather than a condition — it widens what is asked for and orders
 * what comes back, and never narrows anything (D-97).</p>
 *
 * <p>Last, next to "how long", because the two are the same subject.</p>
 */
export const QUESTIONS = [
  { id: "who", prompt: "Who should I ask? Name them with an @handle." },
  { id: "about", prompt: "What is it about?" },
  { id: "long", prompt: "Roughly how long do you need?" },
  { id: "when", prompt: "Roughly when suits? “This week”, a day, or “whenever”." },
] as const;

export type QuestionId = (typeof QUESTIONS)[number]["id"];

export interface AskDraft {
  handle: string | null;
  purpose: string;
  minutes: number | null;
  /** Null until they have been asked. `NO_PREFERENCE` is an answer; null is not. */
  when: Preference | null;
}

export const EMPTY_DRAFT: AskDraft = { handle: null, purpose: "", minutes: null, when: null };

/**
 * The first thing still missing, in a fixed order, or null when the request can be sent.
 *
 * <p>Fixed order rather than "whichever seems most useful": a varying order is a signal, and a
 * signal is something to read. There is nothing here to read.</p>
 */
export function missing(draft: AskDraft): QuestionId | null {
  if (!draft.handle) return "who";
  if (!draft.purpose.trim()) return "about";
  if (!draft.minutes) return "long";
  // Unset, not empty: NO_PREFERENCE is an object and an answer they gave, so it passes here. Only
  // never having been asked returns the question, and asking somebody again after they said
  // "whenever" would be arguing with them.
  if (!draft.when) return "when";
  return null;
}

export function promptFor(id: QuestionId): string {
  return QUESTIONS.find((q) => q.id === id)?.prompt ?? "";
}

/**
 * Fold what the visitor just typed into the request being built.
 *
 * <p>{@code answering} is null for their opening message, which may carry all three at once — "45
 * minutes with @ada about a Series A follow up" is a whole request and should not be interrogated
 * for the sake of it. After that each answer fills the one thing it was asked for, and a reply that
 * happens to mention a handle or a length is read for those too.</p>
 */
export function applyAnswer(draft: AskDraft, answering: QuestionId | null, text: string): AskDraft {
  const parsed = readAsk(text, 0);
  const next: AskDraft = { ...draft };

  if (parsed.handle) next.handle = parsed.handle;
  if (parsed.durationMinutes > 0) next.minutes = parsed.durationMinutes;

  if (answering === null) {
    // Their opening words, whole. What the request is about is for the host's own classifier to
    // decide against the host's own vocabulary (D-15); nothing here summarises it.
    //
    // The stripped version decides only WHETHER they said what it is about — a message that is
    // nothing but a handle and a length has not, and still gets asked (D-80). What gets stored is
    // the sentence they actually typed. Storing the residue instead put a mangled version of their
    // own words in front of the host in quotation marks: "Find 30 mins with @christest to discuss
    // synergy" came out as "Find with to discuss synergy", because taking the mechanics out leaves
    // the connectives that joined them dangling mid-sentence and only the leading ones were
    // cleaned up. The host's inbox quotes this as what the person said, so it has to be.
    if (withoutMechanics(text)) next.purpose = text.trim();
    // "Wednesday with @ada about the lease" is a whole request. Only taken when it reads as one:
    // an opening line we cannot find a day in is not them saying they have no preference.
    //
    // Read from the full text, not from the stripped residue: the day is one of the mechanics
    // withoutMechanics takes out, so reading that would find nothing here, ever.
    const opening = readWhen(text);
    if (!opening.unread && opening.from) next.when = opening;
  } else if (answering === "about") {
    next.purpose = text.trim();
  } else if (answering === "when") {
    next.when = readWhen(text);
  } else if (answering === "who" && !next.handle) {
    // They answered "who" without an @. Take the word anyway rather than asking again: a handle is
    // what a handle looks like, and refusing "ada" for missing a sigil is pedantry.
    const bare = /^[@]?([a-z0-9][a-z0-9._-]{0,38})$/i.exec(text.trim());
    if (bare) next.handle = bare[1].toLowerCase();
  }

  return next;
}

/** What the visitor asked for, once the request is complete. */
export function toNotes(draft: AskDraft): string {
  return draft.purpose.trim();
}

/**
 * Their words with the handle and the length taken out.
 *
 * <p>Used as a <b>test</b>, not as the value: is there anything here besides the mechanics? A
 * message that is only a handle and a length has not said what it is about, and gets asked. What is
 * stored is the untouched sentence.</p>
 *
 * <p>It is deliberately not good enough to store. Removing "@ada" and "45 minutes" leaves the words
 * that joined them stranded wherever they were, and the tidy-up below only reaches the ones at the
 * front — so "45 minutes with @ada about the board pack" cleans up perfectly and "Find 30 mins with
 * @ada to discuss the board pack" becomes "Find with to discuss the board pack". Making the tidy-up
 * cleverer is the wrong move: the fix is not to store an edited copy of somebody's sentence at
 * all.</p>
 */
function withoutMechanics(text: string): string {
  let rest = text
    .replace(/@[a-z0-9][a-z0-9._-]{0,38}/gi, " ")
    .replace(/\d{1,3}\s*(?:minutes|minute|mins|min)\b/gi, " ")
    .replace(/\d{1,2}\s*(?:hours|hour|hrs|hr)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Only the connectives those removals left dangling at the front. Words inside the purpose are
  // none of our business: "a chat about the board pack" keeps its "about".
  for (;;) {
    const shorter = rest.replace(/^(?:with|for|about|and|to|[,.:;-])\s*/i, "");
    if (shorter === rest) break;
    rest = shorter;
  }
  return rest.replace(/[\s,.:;-]+$/, "").trim();
}
